import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import useTranslation from 'next-translate/useTranslation';

import ArrowDown from '@public/icons/angle-down.svg';

interface Option {
  label: string;
  value: string;
}

export interface DropdownProps {
  title?: string;
  placeholder: string;
  options: Option[];
  fieldName: string;
  formik?: any;
  handleChange?: (fieldName: string, value: string | number) => void;
}

type DropdownSelectOptions = {
  label: string;
  value: string;
};

function Dropdown({
  title,
  placeholder,
  fieldName,
  options,
  formik,
  handleChange,
}: Readonly<DropdownProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownSelectOptions>();
  const { t } = useTranslation('common');

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const onSelectValue = (option: any) => {
    setSelectedOption(option);
    setIsOpen(false);
    handleChange?.(fieldName, option.value);
  };

  useEffect(() => {
    if (formik.values && formik.values[fieldName] === '') {
      setSelectedOption({
        label: t(placeholder),
        value: '',
      });
      setIsOpen(false);
    } else {
      const [selected] = options.filter(
        (option) => option.value === formik.values[fieldName]
      );
      setSelectedOption(selected);
    }
  }, [formik]);

  return (
    <>
      {title && (
        <h4 className="text-textColor text-lg leading-[1.6875rem] font-[700] mb-2">
          {t(title)}
        </h4>
      )}
      <div
        className={clsx(
          'bg-white rounded-[10px] relative',
          !isOpen && 'border-grayColor border',
          !title && 'mt-[10px]'
        )}
        data-testid="drop-down-label"
      >
        <div
          className={clsx(
            'px-[14px] pt-[13px] cursor-pointer',
            isOpen &&
              'border-l border-t border-r border-primaryColor rounded-tl-[10px] rounded-tr-[10px]'
          )}
          onClick={toggleDropdown}
          onKeyDown={toggleDropdown}
          role="button"
        >
          <div
            className={clsx(
              'flex items-center justify-between pb-[13px]',
              isOpen ? 'border-b border-grayColor' : 'border-none'
            )}
          >
            <span
              className={clsx(
                selectedOption?.label ? 'text-textColor' : 'text-placeholder'
              )}
              data-testid="placeholder-and-selectedValue"
            >
              {t(selectedOption?.label || '') || t(placeholder)}
            </span>
            <ArrowDown
              className={clsx(
                'transition-all ease-in-out duration-300',
                isOpen ? 'rotate-180' : 'rotate-0'
              )}
            />
          </div>
        </div>

        <div
          className={clsx(
            'transition-all ease-in-out w-full z-21370001 bg-white shadow-[0_7px_15px_0_rgba(42,49,203,0.1)] absolute',
            isOpen
              ? 'duration-500 border-primaryColor border-l border-b border-r rounded-bl-[10px] rounded-br-[10px] pb-[10px] max-h-[205px]'
              : 'max-h-0 duration-300 border-none'
          )}
          data-testid="dropdown-panel"
        >
          <div
            className={clsx(
              'overflow-auto scrollbar-thin scrollbar-thumb-grayColor scrollbar-thumb-rounded-full',
              isOpen ? 'duration-500 max-h-[195px]' : 'max-h-0 duration-300'
            )}
          >
            {options.map((option) => (
              <div
                key={option.value}
                data-testid={option.value}
                className={clsx(
                  'px-[14px] py-[12px] text-textColor hover:bg-dropDownItemHover hover:text-primaryColor cursor-pointer',
                  selectedOption?.value === option.value &&
                    'bg-dropDownItemHover text-primaryColor'
                )}
                onClick={() => onSelectValue(option)}
                onKeyDown={() => onSelectValue(option)}
                role="button"
              >
                {t(option.label)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Dropdown;
